import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Grid,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import ArticleIcon from '@mui/icons-material/Article';
import SchoolIcon from '@mui/icons-material/School';
import RuleIcon from '@mui/icons-material/Rule';

import GenerationProfilesPanel from './GenerationProfilesPanel';
import RequiredResourcesPanel from './RequiredResourcesPanel';
import EntityScopesPanel from './EntityScopesPanel';
import ResourceTypesPanel from './ResourceTypesPanel';
import PromptSpecsPanel from './PromptSpecsPanel';
import ValidationProfilesPanel from './ValidationProfilesPanel';
import GenerationRunsPanel from './GenerationRunsPanel';
import { useParams, useNavigate } from 'react-router-dom';
import { ADMIN_URL_TOOLBOX_CONFIG } from '../../PageStructure/admin-values';

type TuningArea =
  | 'agent'
  | 'entity-documentation'
  | 'course-generation'
  | 'validation-runs';

const AGENT_TABS = [
  'agent-profiles',
  'agent-prompt-building-blocks'
];
type AgentTab = typeof AGENT_TABS[number];

const ENTITY_DOCUMENTATION_TABS = [
  'entity-doc-profiles',
  'required-resources',
  'entity-scopes',
  'resource-types',
  'discovery-generation',
];
type EntityDocumentationTab = typeof ENTITY_DOCUMENTATION_TABS[number];

const COURSE_GENERATION_TABS = [
  'course-profiles',
  'course-sections',
  'course-prompt-guidance',
];
type CourseGenerationTab = typeof COURSE_GENERATION_TABS[number];

const VALIDATION_RUNS_TABS = [
  'validation',
  'runs',
]
type ValidationRunsTab = typeof VALIDATION_RUNS_TABS[number];

const areaDescriptions: Record<TuningArea, {
  title: string;
  icon: React.ReactNode;
  description: string;
  chips: string[];
}> = {
  agent: {
    title: 'AI Agent Tuning',
    icon: <SmartToyIcon color="primary" />,
    description:
      'Controls the AI agent behavior shared across workflows: model selection, temperature, prompt assembly flags, RAG inclusion, reusable instruction fragments, and global generation defaults.',
    chips: [
      'Model behavior',
      'Prompt assembly',
      'RAG usage',
      'Shared instructions',
    ],
  },
  'entity-documentation': {
    title: 'Entity Documentation Tuning',
    icon: <ArticleIcon color="primary" />,
    description:
      'Controls how entity documentation resources are generated, scoped, discovered, and validated. This is where you tune what/why/sell/configure/validate resource requirements and entity boundaries.',
    chips: [
      'Entity resources',
      'Required outputs',
      'Scope boundaries',
      'Discovery prompts',
    ],
  },
  'course-generation': {
    title: 'Course Generation Tuning',
    icon: <SchoolIcon color="primary" />,
    description:
      'Controls snackable course generation: course-section generation profiles, section prompt templates, product reference guidance, source-priority instructions, and depth guidance.',
    chips: [
      'Snackable courses',
      'Course sections',
      'Quiz context',
      'Source grounding',
    ],
  },
  'validation-runs': {
    title: 'Validation and Run Observability',
    icon: <RuleIcon color="primary" />,
    description:
      'Controls validation rubrics and provides operational visibility into generation runs, validation runs, question-level results, prompts, RAG context, and generated outputs.',
    chips: [
      'Validation profiles',
      'Rubric questions',
      'Run history',
      'Debug output',
    ],
  },
};

const SectionIntro: React.FC<{
  area: TuningArea;
  children?: React.ReactNode;
}> = ({ area, children }) => {
  const config = areaDescriptions[area];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {config.icon}

          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700}>
              {config.title}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {config.description}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {config.chips.map((chip) => (
            <Chip key={chip} size="small" variant="outlined" label={chip} />
          ))}
        </Stack>

        {children}
      </Stack>
    </Paper>
  );
};

const GuidanceCard: React.FC<{
  title: string;
  description: string;
  bullets: string[];
}> = ({ title, description, bullets }) => (
  <Paper variant="outlined" sx={{ p: 1.5, height: '100%', bgcolor: 'grey.50' }}>
    <Typography variant="subtitle2" fontWeight={700}>
      {title}
    </Typography>

    <Typography variant="body2" color="text.secondary" mt={0.5}>
      {description}
    </Typography>

    <Box component="ul" sx={{ pl: 2.5, mb: 0, mt: 1 }}>
      {bullets.map((bullet) => (
        <Typography key={bullet} component="li" variant="caption" color="text.secondary">
          {bullet}
        </Typography>
      ))}
    </Box>
  </Paper>
);

const AgentGuidance: React.FC = () => (
  <Grid container spacing={1.5}>
    <Grid item xs={12} md={6}>
      <GuidanceCard
        title="Generation Profiles"
        description="Use this when the AI behavior itself needs to change."
        bullets={[
          'Model, temperature, token limits, limiter key',
          'Whether RAG, entity scope, section instructions, and product references are included',
          'Workflow kind: entity_resource, course_section, or validation',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={6}>
      <GuidanceCard
        title="Prompt Building Blocks"
        description="Use this for shared instruction fragments reused by multiple workflows."
        bullets={[
          'Depth guidance: high-level, medium, detailed',
          'Authoritative resource usage and conflict handling',
          'High-level entity behavior and product reference rules',
        ]}
      />
    </Grid>
  </Grid>
);

const EntityDocumentationGuidance: React.FC = () => (
  <Grid container spacing={1.5}>
    <Grid item xs={12} md={6} xl={3}>
      <GuidanceCard
        title="Entity Doc Profiles"
        description="Model and prompt-assembly settings for entity resource generation."
        bullets={[
          'Kind should usually be entity_resource',
          'Can be scoped by entity_type and resource_type',
          'Controls RAG usage and generation requirements',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={6} xl={3}>
      <GuidanceCard
        title="Required Resources"
        description="Defines which outputs are expected for each entity type and workflow."
        bullets={[
          'What, why, sell, configure, validate, differentiate, troubleshoot',
          'Entity documentation versus snackable course workflow availability',
          'Required versus optional outputs',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={6} xl={3}>
      <GuidanceCard
        title="Entity Scopes and Resource Types"
        description="Defines boundaries and section-specific prompt behavior."
        bullets={[
          'Entity hierarchy and strict boundaries',
          'Resource/section prompt templates',
          'Content focus and content boundaries',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={6} xl={3}>
      <GuidanceCard
        title="Discovery Generation"
        description="Controls DB-backed prompts for discovering child entities."
        bullets={[
          'Product components',
          'Product use cases',
          'Security use-case components',
        ]}
      />
    </Grid>
  </Grid>
);

const CourseGenerationGuidance: React.FC = () => (
  <Grid container spacing={1.5}>
    <Grid item xs={12} md={4}>
      <GuidanceCard
        title="Course Profiles"
        description="Model and prompt-assembly settings for snackable course sections."
        bullets={[
          'Kind should usually be course_section',
          'Controls model, temperature, RAG, and prompt flags',
          'Used by DBPromptBuilder during section generation',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={4}>
      <GuidanceCard
        title="Course Sections"
        description="Defines what each course section should contain."
        bullets={[
          'What, why, sell, configure, validate, differentiate, troubleshoot',
          'Prompt templates, content focus, and boundaries',
          'Default depth and procedural-markdown expectations',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={4}>
      <GuidanceCard
        title="Course Prompt Guidance"
        description="Shared instructions applied during course prompt construction."
        bullets={[
          'Depth guidance',
          'Authoritative source priority',
          'Product reference requirements',
          'High-level entity behavior',
        ]}
      />
    </Grid>
  </Grid>
);

const ValidationRunsGuidance: React.FC = () => (
  <Grid container spacing={1.5}>
    <Grid item xs={12} md={6}>
      <GuidanceCard
        title="Validation Profiles"
        description="Controls the critic/evaluator behavior."
        bullets={[
          'Evaluation model and temperature',
          'Publishable and needs-review thresholds',
          'Rubric questions and blocking failures',
        ]}
      />
    </Grid>

    <Grid item xs={12} md={6}>
      <GuidanceCard
        title="Runs"
        description="Operational view for troubleshooting generation quality."
        bullets={[
          'Generated output and prompts',
          'RAG query and RAG context',
          'Validation results and question-level evidence',
          'Version/deployment scope visibility',
        ]}
      />
    </Grid>
  </Grid>
);

const AITuningView: React.FC = () => {

  const { tab2, tab3 } = useParams();
  const area = (areaDescriptions[tab2] ? tab2 : 'agent') as TuningArea;
  const navigate = useNavigate();
  const setArea = (area: TuningArea): void => navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/ai/tuning/${area}`);
  const agentTab: AgentTab = AGENT_TABS.includes(tab3) ? tab3 : AGENT_TABS[0];
  const entityDocumentationTab = ENTITY_DOCUMENTATION_TABS.includes(tab3) ? tab3 : ENTITY_DOCUMENTATION_TABS[0];
  const setTab = (tabName): void => navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/ai/tuning/${area}/${tabName}`);
  const courseGenerationTab = COURSE_GENERATION_TABS.includes(tab3) ? tab3 : COURSE_GENERATION_TABS[0];
  const validationRunsTab = VALIDATION_RUNS_TABS.includes(tab3) ? tab3 : VALIDATION_RUNS_TABS[0];

  return (
    <Stack spacing={2}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TuneIcon color="primary" />

          <Box>
            <Typography variant="h6" fontWeight={700}>
              AI Pipeline Tuning
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Tune AI behavior by workflow area: the AI Agent as a whole, entity documentation generation,
              snackable course generation, and validation/run observability.
            </Typography>
          </Box>
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          Changes here affect future generation and validation runs. Existing generated
          resources are not automatically regenerated unless you trigger generation again.
        </Alert>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Tabs
          value={area}
          onChange={(_, value) => setArea(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab
            value="agent"
            icon={<SmartToyIcon fontSize="small" />}
            iconPosition="start"
            label="AI Agent"
          />

          <Tab
            value="entity-documentation"
            icon={<ArticleIcon fontSize="small" />}
            iconPosition="start"
            label="Entity Documentation"
          />

          <Tab
            value="course-generation"
            icon={<SchoolIcon fontSize="small" />}
            iconPosition="start"
            label="Course Generation"
          />

          <Tab
            value="validation-runs"
            icon={<RuleIcon fontSize="small" />}
            iconPosition="start"
            label="Validation & Runs"
          />
        </Tabs>

        {area === 'agent' ? (
          <Stack spacing={2}>
            <SectionIntro area="agent">
              <AgentGuidance />
            </SectionIntro>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Tabs
                value={agentTab}
                onChange={(_, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab value="agent-profiles" label="Generation Profiles" />
                <Tab value="agent-prompt-building-blocks" label="Prompt Building Blocks" />
              </Tabs>

              {agentTab === 'agent-profiles' ? (
                <GenerationProfilesPanel
                  title="AI Agent Generation Profiles"
                  description="Global view of model behavior, prompt assembly, RAG usage, limiter keys, and workflow-specific generation profiles."
                  queryKeySuffix="agent"
                />
              ) : null}

              {agentTab === 'agent-prompt-building-blocks' ? (
                <PromptSpecsPanel
                  visibleTabs={[
                    'depth',
                    'high-level',
                    'authoritative',
                    'product-reference',
                  ]}
                  initialTab="depth"
                />
              ) : null}
            </Paper>
          </Stack>
        ) : null}

        {area === 'entity-documentation' ? (
          <Stack spacing={2}>
            <SectionIntro area="entity-documentation">
              <EntityDocumentationGuidance />
            </SectionIntro>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Tabs
                value={entityDocumentationTab}
                onChange={(_, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab value="entity-doc-profiles" label="Entity Doc Profiles" />
                <Tab value="required-resources" label="Required Resources" />
                <Tab value="entity-scopes" label="Entity Scopes" />
                <Tab value="resource-types" label="Resource Types" />
                <Tab value="discovery-generation" label="Discovery Generation" />
              </Tabs>

              {entityDocumentationTab === 'entity-doc-profiles' ? (
                <GenerationProfilesPanel
                  title="Entity Documentation Generation Profiles"
                  description="Controls model behavior, RAG usage, prompt assembly, and generation requirements for entity documentation resources."
                  kindFilter="entity_resource"
                  defaultKind="entity_resource"
                  allowedKinds={['entity_resource']}
                  queryKeySuffix="entity-documentation"
                />
              ) : null}

              {entityDocumentationTab === 'required-resources' ? <RequiredResourcesPanel /> : null}
              {entityDocumentationTab === 'entity-scopes' ? <EntityScopesPanel /> : null}
              {entityDocumentationTab === 'resource-types' ? <ResourceTypesPanel /> : null}

              {entityDocumentationTab === 'discovery-generation' ? (
                <PromptSpecsPanel
                  visibleTabs={['discovery']}
                  initialTab="discovery"
                />
              ) : null}
            </Paper>
          </Stack>
        ) : null}

        {area === 'course-generation' ? (
          <Stack spacing={2}>
            <SectionIntro area="course-generation">
              <CourseGenerationGuidance />
            </SectionIntro>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Tabs
                value={courseGenerationTab}
                onChange={(_, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab value="course-profiles" label="Course Profiles" />
                <Tab value="course-sections" label="Course Sections" />
                <Tab value="course-prompt-guidance" label="Course Prompt Guidance" />
              </Tabs>

              {courseGenerationTab === 'course-profiles' ? (
                <GenerationProfilesPanel
                  title="Course Section Generation Profiles"
                  description="Controls model behavior, RAG usage, prompt assembly, and source-grounding flags used by snackable course section generation."
                  kindFilter="course_section"
                  defaultKind="course_section"
                  allowedKinds={['course_section']}
                  queryKeySuffix="course-generation"
                />
              ) : null}

              {courseGenerationTab === 'course-sections' ? <ResourceTypesPanel /> : null}

              {courseGenerationTab === 'course-prompt-guidance' ? (
                <PromptSpecsPanel
                  visibleTabs={[
                    'depth',
                    'high-level',
                    'authoritative',
                    'product-reference',
                  ]}
                  initialTab="depth"
                />
              ) : null}
            </Paper>
          </Stack>
        ) : null}

        {area === 'validation-runs' ? (
          <Stack spacing={2}>
            <SectionIntro area="validation-runs">
              <ValidationRunsGuidance />
            </SectionIntro>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Tabs
                value={validationRunsTab}
                onChange={(_, value) => setTab(value)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
              >
                <Tab value="validation" label="Validation Tuning" />
                <Tab value="runs" label="Runs" />
              </Tabs>

              {validationRunsTab === 'validation' ? <ValidationProfilesPanel /> : null}
              {validationRunsTab === 'runs' ? <GenerationRunsPanel /> : null}
            </Paper>
          </Stack>
        ) : null}
      </Paper>
    </Stack>
  );
};

export default AITuningView;